import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { flatClient, flatClientDetails, rejectValue } from 'redux/helpers';

import { showSuccessMessage, showErrorMessage } from '../notification';
import { DELETE_CLIENT, GET_CLIENTS, GET_CLIENT_BY_ID } from 'services/api';
import {
  CLIENT,
  CLIENT_ALL,
  CLIENT_DELETE,
  CLIENT_SEARCH,
  CLIENT_VIEW,
} from './types';
import { clientDetailsActions as TEXT } from 'locales/en/client';
const clientAdapter = createEntityAdapter();

export const initialState = clientAdapter.getInitialState({
  isLoading: false,
  detailsIsLoading: false,
  pagination: {
    total: 4,
    total_pages: 1,
    current_page: 1,
    per_page: 25,
    count: 4,
  },
  params: {
    per_page: 25,
  },
  search: {
    term: '',
    submitted: false,
  },
});

export const getClients = createAsyncThunk(
  CLIENT_ALL,
  async (_, { rejectWithValue, getState }) => {
    try {
      const companyId = getState().company.current;
      const { per_page } = getState().client.params;

      const { data, meta } = await GET_CLIENTS({
        per_page,
        company_id: companyId,
        include:
          'integration,liableCompany,defaultRole,defaultFeeStructure,companies',
      });

      if (!data.length) return { data: [], pagination: meta.pagination };
      const _data = data.map(flatClient);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(
        rejectValue({ ...error.response, skipNotification: true })
      );
    }
  }
);

export const getClinetById = createAsyncThunk(
  CLIENT_VIEW,
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await GET_CLIENT_BY_ID(id);

      return flatClientDetails(data);
    } catch (error) {
      return rejectWithValue(
        rejectValue({ ...error.response, skipNotification: true })
      );
    }
  }
);

export const searchClients = createAsyncThunk(
  CLIENT_SEARCH,
  async (_, { rejectWithValue, getState }) => {
    try {
      const companyId = getState().company.current;
      const { term } = getState().client.search;

      if (!term) {
        return;
      }

      const { data, meta } = await GET_CLIENTS({
        term,
        company_id: companyId,
        include:
          'integration,liableCompany,defaultRole,defaultFeeStructure,companies',
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      const _data = data.map(flatClient);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const deleteClient = createAsyncThunk(
  CLIENT_DELETE,
  async (clientId, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await DELETE_CLIENT(clientId);

      dispatch(showSuccessMessage(TEXT.deleteSuccess));
      dispatch(getClients());

      return { data: data };
    } catch (error) {
      dispatch(showErrorMessage(TEXT.deleteSuccess));

      return rejectWithValue(
        rejectValue({ ...error.response, skipNotification: true })
      );
    }
  }
);

const clientSlice = createSlice({
  name: CLIENT,
  initialState,
  reducers: {
    setParams: (state, { payload }) => {
      state.params = { ...state.params, ...payload };
    },
    setSearchTerm: (state, { payload }) => {
      state.search.term = payload;
    },
    resetSearch: (state) => {
      state.search = initialState.search;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClients.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = false;
      })
      .addCase(getClients.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getClients.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        clientAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(searchClients.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = true;
      })
      .addCase(searchClients.rejected, (state) => {
        state.isLoading = false;
        state.search.submitted = false;
      })
      .addCase(searchClients.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        clientAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(getClinetById.pending, (state) => {
        state.detailsIsLoading = true;
      })
      .addCase(getClinetById.rejected, (state) => {
        state.detailsIsLoading = false;
      })
      .addCase(getClinetById.fulfilled, (state, { payload }) => {
        state.detailsIsLoading = false;
        clientAdapter.upsertOne(state, payload);
      })
      .addDefaultCase((state) => state);
  },
});

export const clientSelectors = clientAdapter.getSelectors(
  (state) => state.client
);

export const clientByIdSelector = (state, id) => ({
  data: clientSelectors.selectById(state, id),
  isLoading: state.client.detailsIsLoading,
});

export const clientAllSelector = (state) => ({
  data: clientSelectors.selectAll(state),
  pagination: state.client.pagination,
  isLoading: state.client.isLoading,
  params: state.client.params,
  searchTerm: state.client.search.term,
});

export const { setParams, setSearchTerm, resetSearch } = clientSlice.actions;

export default clientSlice.reducer;
